// Libraries
import React, { useState, useEffect } from 'react'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import { Typography, Box, IconButton, Alert } from '@mui/material'
import { DataGrid, GridColDef, GridColumnResizeParams } from '@mui/x-data-grid'
import dayjs from 'dayjs'

// Types and Interfaces
import { TableData } from '../../../../../types/table'

// Utils
import { devError } from '../../../../../utils/feedback'
import { GRID_LOCALE_TEXT } from '../../../../../utils/tableLocaleText'
import { timesheetStatusTranslations } from '../../../../../utils/translationsDicts'

// Components
import Driver from './Driver/Driver'
import ErrorCell from './ErrorCell/ErrorCell'

interface MassApprovalTableProps {
	data: TableData
}

type Row = {
	driver: React.ReactNode
	nr: number
	status: string
	date: string
	services: number
	errorCell: React.ReactNode
}

/**
 * ## Renders a table for mass approval feedback details.
 * - @param data - The data to be displayed in the table. Of type MassApprovalFeedbackPartial
 * - @returns - JSX.Element representing the MassApprovalTable component for mass approval feedback details.
 */
const MassApprovalTable: React.FC<MassApprovalTableProps> = ({ data }) => {
	// TODO: Refactor localstorge to use a more robust state management solution wen the User Setting Module is ready
	const columns: GridColDef<(typeof notApprovedTableRows)[number]>[] = [
		{
			field: 'driver',
			headerName: 'Motorista',
			width: 200,
			renderCell: params => {
				const driverData = params.row.driver // Access the driver field
				return driverData // Render the Driver component
			},
		},
		{
			field: 'nr',
			headerName: 'Nr',
			width: 90,
		},
		{
			field: 'status',
			headerName: 'Estado',
			width: 200,
		},
		{
			field: 'date',
			headerName: 'Data',
			width: 100,
		},
		{
			field: 'services',
			headerName: 'Serviços',
			width: 90,
		},
		{
			field: 'errorCell',
			headerName: 'Erro',
			width: 400,
			renderCell: params => {
				return params.value
			},
		},
	]

	const [notApprovedTableRows, setNotApprovedTableRows] = useState<Row[]>([])
	const [isNotApprovedCompact, setIsNotApprovedCompact] = useState(
		localStorage.getItem('massApprovalTablePreferences')
			? JSON.parse(localStorage.getItem('massApprovalTablePreferences') || '{}').isNotApprovedCompact
			: true,
	)
	const [tableColumns, setTableColumns] = useState<GridColDef[]>(() => {
		const savedPreferences = localStorage.getItem('massApprovalTablePreferences')
		if (savedPreferences) {
			try {
				const parsedPreferences = JSON.parse(savedPreferences)
				if (Array.isArray(parsedPreferences.columnWidths)) {
					return parsedPreferences.columnWidths
				}
			} catch (error) {
				devError('[MassApprovalTable.tsx] - Failed to parse table preferences from localStorage:', error)
			}
		}
		return columns // Fallback to default columns
	})

	const [tablePreferences, setTablePreferences] = useState(() => {
		const savedPreferences = localStorage.getItem('massApprovalTablePreferences')
		return savedPreferences ? JSON.parse(savedPreferences) : {}
	})

	/**
	 * Handles the change in column width for the data grid.
	 * This function updates the column widths in the state and saves the preferences to localStorage, ensuring that user customizations persist across sessions.
	 * It also recalculates the total table width based on the new column widths to ensure the layout remains consistent.
	 * @param params - The parameters containing the column definition and the new width.
	 * @see GridColumnResizeParams - The type of the parameters passed to this function, which includes details about the column being resized and its new width.
	 * @see savePreferences - A helper function that saves the updated column widths and other preferences to localStorage for persistence.
	 */
	const handleColumnWidthChange = (params: GridColumnResizeParams) => {
		const updatedColumns = tableColumns?.map(col =>
			col.field === params.colDef.field ? { ...col, width: params.width } : col,
		)

		setTableColumns(updatedColumns)

		savePreferences({ columnWidths: updatedColumns }) // Use updatedColumns here
	}

	/**
	 * Saves the updated table preferences to localStorage.
	 * This function merges the new preferences with the existing ones and saves them to localStorage under the key 'massApprovalTablePreferences'. It ensures that user customizations, such as column widths and compact view settings, are persisted across sessions for a consistent user experience.
	 * @param newPreferences - The new preferences to be saved.
	 * @see handleColumnWidthChange - The function that handles column width changes and calls this function to save the updated preferences.
	 * @see isSuccessCompact, isNotApprovedCompact - State variables that manage the compact view settings for the approved and not approved tables, which are also saved as part of the preferences.
	 */
	const savePreferences = (newPreferences: Partial<typeof tablePreferences>) => {
		const updatedPreferences = { ...tablePreferences, ...newPreferences }
		setTablePreferences(updatedPreferences)
		localStorage.setItem('massApprovalTablePreferences', JSON.stringify(updatedPreferences))
	}

	/**
	 * Effect hook that runs on component mount to retrieve and apply saved table preferences from localStorage, ensuring that the `driver` field in the column definitions is always rendered as a React component. This is crucial for maintaining the correct rendering of the driver information in the table, especially after user customizations such as column width changes. The effect checks for saved preferences, parses them, and updates the column definitions accordingly to ensure a consistent and functional user interface.
	 * @see localStorage - The web storage API used to save and retrieve user preferences for the table.
	 * @see tableColumns - The state variable that holds the current column definitions for the data grid, which is updated based on the retrieved preferences.
	 * @see Driver - The React component used to render the driver information in the table, which is ensured to be used in the column definitions for the `driver` field.
	 * @see savePreferences - The function that saves updated preferences to localStorage, which may trigger this effect on subsequent renders to apply the saved settings.
	 */
	useEffect(() => {
		const savedPreferences = localStorage.getItem('massApprovalTablePreferences')
		if (savedPreferences) {
			try {
				const parsedPreferences = JSON.parse(savedPreferences)
				if (Array.isArray(parsedPreferences.columnWidths)) {
					const updatedColumns = parsedPreferences.columnWidths.map((col: GridColDef) => {
						if (col.field === 'driver') {
							return {
								...col,
								renderCell: (params: { row: { driver: unknown } }) => params.row.driver, // Ensure `driver` is rendered as a React component
							}
						}
						return col
					})
					setTableColumns(updatedColumns)
				}
			} catch (error) {
				devError('[MassApprovalTable.tsx] - Failed to parse table preferences from localStorage:', error)
			}
		}
	}, [])

	/**
	 * Effect hook that runs whenever the `data` prop changes to update the rows for both approved and not approved timesheets. It maps the API response data to the format required by the DataGrid component, ensuring that the driver information is rendered using the Driver component and that all necessary fields are correctly formatted for display. This effect ensures that the table reflects the latest data from the mass approval process, providing users with accurate feedback on which timesheets were approved or not approved.
	 * @see data - The prop containing the mass approval feedback data, which is used to populate the rows of the approved and not approved tables.
	 * @see setApprovedTableRows, setNotApprovedTableRows - State setters that update the rows for the approved and not approved tables based on the processed data.
	 * @see Driver - The React component used to render the driver information in the table rows, ensuring a consistent and visually appealing display of driver details.
	 * @see timesheetStatusTranslations - A utility object used to translate timesheet submission statuses into user-friendly text for display in the table.
	 */
	useEffect(() => {
		if (data !== null || data !== undefined) {
			if (data?.feedback?.failed !== null && data?.feedback?.failed.length > 0) {
				setNotApprovedTableRows(
					data?.feedback?.failed.map((item, index) => {
						return {
							id: index,
							driver: (
								<Driver
									pictureB16={item.pictureB16}
									driverName={item.driverName}
									isCompact={isNotApprovedCompact}
								/>
							),
							nr: item.driverNr,
							status:
								timesheetStatusTranslations[item.timesheetSubmissionStatus] || item.timesheetSubmissionStatus || 'N/A',
							date: dayjs(item.timesheetDate).format('DD/MM/YYYY'),
							services: item.serviceCount,
							errorCell: <ErrorCell item={item} />,
						}
					}),
				)
			}
		}
	}, [data, data?.feedback?.failed, isNotApprovedCompact])

	return (
		<div id='mass-approval-table'>
			{data?.feedback?.approved !== null && data?.feedback?.approved > 0 && (
				<Box
					sx={{
						marginBottom: '1rem',
						display: 'grid',
						gap: '1rem',
					}}
				>
					<Typography
						sx={{
							fontFamily: 'var(--font-family)',
							fontSize: '20px',
							fontStyle: 'normal',
							fontWeight: 600,
							lineHeight: '160%',
							color: 'var(--primary-dark)',
						}}
					>
						{data?.feedback?.approved ?? 0} Aprovada{data?.feedback?.approved !== 1 && 's'} com Sucesso
					</Typography>
					<Alert
						variant='filled'
						severity='success'
						title={`${data?.feedback?.approved ?? 0} Folhas de Ponto sem Alterações Aprovadas. Poderá consultar as mesmas através da área Processadas`}
						aria-description='Alerta de aprovação em massa bem-sucedida'
						sx={{
							height: '37px',
							padding: '0 16px',
							fontFamily: 'var(--font-family)',
							fontSize: '14px',
							fontWeight: 500,
							letterSpacing: '0.15px',
							lineHeight: '150%',
							background: 'linear-gradient(86deg, #16C53D -57.78%, #00A5FF 115.35%)',
						}}
					>
						{`${data?.feedback?.approved ?? 0} Folhas de Ponto sem Alterações Aprovadas. Poderá consultar as mesmas através da área Processadas`}
					</Alert>
				</Box>
			)}
			<Box
				sx={{
					marginBottom: '1rem',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
				}}
			>
				<Typography
					sx={{
						fontFamily: 'var(--font-family)',
						fontSize: '20px',
						fontStyle: 'normal',
						fontWeight: 600,
						lineHeight: '160%',
						color: 'var(--primary-dark)',
					}}
				>
					{notApprovedTableRows?.length} Não {notApprovedTableRows?.length > 1 ? 'Foram' : 'Foi'} Possíve
					{notApprovedTableRows?.length > 1 ? 'is de' : 'l'} Aprovar
				</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
					<IconButton
						onClick={() => {
							setIsNotApprovedCompact(false)
							savePreferences({ isNotApprovedCompact: false })
						}}
						sx={{
							borderRadius: 0,
							padding: '2px',
						}}
					>
						<TableRowsIcon
							color={!isNotApprovedCompact ? 'primary' : 'disabled'}
							fontSize='small'
							sx={{
								width: '16px',
								height: '16px',
							}}
						/>
					</IconButton>
					<IconButton
						onClick={() => {
							setIsNotApprovedCompact(true)
							savePreferences({ isNotApprovedCompact: true })
						}}
						sx={{
							borderRadius: 0,
							padding: '2px',
						}}
					>
						<ViewHeadlineIcon
							color={isNotApprovedCompact ? 'primary' : 'disabled'}
							fontSize='small'
							sx={{
								width: '16px',
								height: '16px',
							}}
						/>
					</IconButton>
				</Box>
			</Box>
			<Alert
				variant='filled'
				severity='error'
				title='Folhas de Ponto sem Alterações Não Aprovadas.'
				aria-description='Alerta de aprovação em massa para folhas de ponto não aprovadas'
				sx={{
					height: '37px',
					padding: '0 16px',
					fontFamily: 'var(--font-family)',
					fontSize: '14px',
					fontWeight: 500,
					letterSpacing: '0.15px',
					lineHeight: '150%',
					background: 'linear-gradient(90deg, #D32F2F 0%, #FB736D 100%)',
					marginBottom: '1rem',
				}}
			>
				As seguintes Folhas de Ponto não foram possíveis de processar devido a problemas relacionados com o Cálculo da
				Disponibilidade e/ou Aprovação.
				<br />
				Por favor, verifique a(s) Folha(s) de de Ponto. Poderá enviar o erro à equipa de suporte, recorrendo ao botão
				copiar referente a cada Folha.
			</Alert>
			<DataGrid
				rows={notApprovedTableRows}
				columns={tableColumns}
				onColumnWidthChange={handleColumnWidthChange}
				initialState={{
					pagination: {
						paginationModel: {
							pageSize: 5,
						},
					},
				}}
				pageSizeOptions={[5]}
				disableRowSelectionOnClick
				clipboardCopyCellDelimiter=''
				hideFooterPagination={notApprovedTableRows?.length <= 5}
				localeText={GRID_LOCALE_TEXT}
				density={isNotApprovedCompact ? 'compact' : 'standard'}
				slotProps={{
					cell: {
						title: undefined,
					},
				}}
				sx={{
					height: 'auto',
					'& .MuiDataGrid-root': {
						borderColor: 'rgba(0, 0, 0, 0)',
					},
					'& .MuiDataGrid-columnHeaders': {
						'& .MuiDataGrid-row': {
							fontFamily: 'var(--font-family)',
							fontSize: '12px',
							fontStyle: 'normal',
							fontWeight: 600,
							lineHeight: '200%',
							color: 'var(--primary-light)',
						},
					},
					'& .MuiDataGrid-row': {
						fontFamily: 'var(--font-family)',
						fontSize: '12px',
						fontStyle: 'normal',
						fontWeight: 400,
						lineHeight: '130%',
						color: '#092056',
					},
					'& .MuiDataGrid-iconSeparator': {
						fill: 'rgba(145 145 145 / 30%)',
					},
					'& .MuiDataGrid-footerContainer': {
						display: notApprovedTableRows?.length <= 5 ? 'none' : 'flex',
					},
					'& .MuiTablePagination-displayedRows': {
						fontFamily: 'var(--font-family)',
						fontSize: '11px',
						fontStyle: 'normal',
						fontWeight: 400,
						lineHeight: '130%',
					},
					'& .MuiSvgIcon-root': {
						color: 'var(--primary)',
					},
				}}
			/>
		</div>
	)
}

export default MassApprovalTable
